import { useStableCallback } from '@main/shared/browser';
import { Table } from '@tanstack/react-table';
import { useEffect, useState } from 'react';

function calculateOverflow(element: HTMLElement) {
  const { scrollWidth, clientWidth } = element;
  return scrollWidth > clientWidth;
}

export function useTableOverflow<T>(tableRef: React.RefObject<HTMLElement>, table: Table<T>) {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const { columnSizing, columnVisibility } = table.getState();

  const updateOverflow = useStableCallback(() => {
    if (tableRef.current) {
      const newOverflow = calculateOverflow(tableRef.current);
      setIsOverflowing(newOverflow);
    }
  });

  useEffect(() => {
    const element = tableRef.current;
    if (!element) return;

    const observer = new ResizeObserver(() => {
      updateOverflow();
    });
    observer.observe(element);

    updateOverflow();

    return () => {
      observer.disconnect();
    };
  }, [tableRef, updateOverflow]);

  useEffect(() => {
    updateOverflow();
  }, [columnSizing, columnVisibility, updateOverflow]);

  return isOverflowing;
}
